<template>
    <div v-if="visible" class="profile-edit-overlay" @click.self="closeDialog">
        <section class="profile-edit-dialog" :class="{ 'profile-edit-dialog--makeup': professionalType === 'makeupArtist' }" role="dialog" aria-modal="true" aria-labelledby="profileEditTitle">
            <header>
                <div><p>PROFILE MAINTENANCE</p><h3 id="profileEditTitle">编辑</h3><span>保存时会向当前填写的联系电话发送验证码；资料即时生效，无需审核。</span></div>
                <button type="button" aria-label="关闭基础资料编辑" @click="closeDialog">×</button>
            </header>
            <form novalidate @submit.prevent="submitProfile">
                <main>
                    <role-portrait-upload
                        class="profile-edit-full"
                        v-model="form.personalPhoto"
                        :disabled="portraitStatus.canSubmit !== true"
                        :disabled-message="portraitStatus.message || '当前暂不能更换本人真实照片'"
                        @change="formError = ''"
                        @error="formError = $event" />
                    <label><span>联系电话 <b>*</b></span><input v-model.trim="form.phone" inputmode="numeric" maxlength="11" placeholder="请输入中国大陆手机号" /></label>
                    <label><span>短信验证码 <b>*</b></span><span class="profile-edit-code"><input v-model.trim="form.smsCode" inputmode="numeric" maxlength="6" placeholder="6 位验证码" /><button type="button" :disabled="smsSending || smsCooldown > 0" @click="requestSmsCode">{{ smsButtonText }}</button></span></label>
                    <label><span>省份 <b>*</b></span><div class="profile-edit-select"><button type="button" class="profile-edit-select-trigger" :class="{ 'is-placeholder': !form.provinceCode }" :aria-expanded="openSelect === 'province'" aria-haspopup="listbox" @click="toggleSelect('province')">{{ selectedOptionLabel(provinceOptions, form.provinceCode, '请选择省份') }}<i aria-hidden="true">⌄</i></button><div v-if="openSelect === 'province'" class="profile-edit-select-menu" role="listbox" aria-label="省份"><button v-for="item in provinceOptions" :key="item.value" type="button" role="option" :aria-selected="form.provinceCode === item.value" :class="{ active: form.provinceCode === item.value }" @click="selectProvince(item.value)">{{ item.label }}<i v-if="form.provinceCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                    <label><span>城市 <b>*</b></span><div class="profile-edit-select"><button type="button" class="profile-edit-select-trigger" :class="{ 'is-placeholder': !form.cityCode }" :disabled="!form.provinceCode" :aria-expanded="openSelect === 'city'" aria-haspopup="listbox" @click="toggleSelect('city')">{{ selectedOptionLabel(cityOptions, form.cityCode, '请选择城市') }}<i aria-hidden="true">⌄</i></button><div v-if="openSelect === 'city'" class="profile-edit-select-menu" role="listbox" aria-label="城市"><button v-for="item in cityOptions" :key="item.value" type="button" role="option" :aria-selected="form.cityCode === item.value" :class="{ active: form.cityCode === item.value }" @click="selectCity(item.value)">{{ item.label }}<i v-if="form.cityCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                    <label class="profile-edit-full"><span>区 / 县 <b>*</b></span><div class="profile-edit-select"><button type="button" class="profile-edit-select-trigger" :class="{ 'is-placeholder': !form.districtCode }" :disabled="!form.cityCode" :aria-expanded="openSelect === 'district'" aria-haspopup="listbox" @click="toggleSelect('district')">{{ selectedOptionLabel(districtOptions, form.districtCode, '请选择区 / 县') }}<i aria-hidden="true">⌄</i></button><div v-if="openSelect === 'district'" class="profile-edit-select-menu" role="listbox" aria-label="区或县"><button v-for="item in districtOptions" :key="item.value" type="button" role="option" :aria-selected="form.districtCode === item.value" :class="{ active: form.districtCode === item.value }" @click="selectDistrict(item.value)">{{ item.label }}<i v-if="form.districtCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                    <label class="profile-edit-full"><span>专业简介 <b>*</b></span><textarea v-model="form.professionalIntro" maxlength="2000" rows="5" placeholder="介绍服务经验、服务范围与擅长方向。"></textarea><small>{{ form.professionalIntro.length }}/2000</small></label>
                    <p v-if="formError" class="profile-edit-error" role="alert">{{ formError }}</p>
                </main>
                <footer><button type="button" :disabled="submitting" @click="closeDialog">取消</button><button type="submit" class="profile-edit-primary" :disabled="submitting">{{ submitting ? '正在保存…' : '保存' }}</button></footer>
            </form>
        </section>
    </div>
</template>

<script>
import qs from "qs";
import { regionData } from "element-china-area-data";
import request from "@/tool/request";
import API from "@/tool/api";
import developmentSmsCode from "@/tool/developmentSmsCode.cjs";
import { getResultData, getResultMessage, isResultSuccess } from "@/tool/result";
import { buildProfileUpdateSubmission } from "@/tool/portraitSubmission.cjs";
import RolePortraitUpload from "./RolePortraitUpload";

const emptyForm = () => ({ phone: "", smsCode: "", provinceCode: "", cityCode: "", districtCode: "", professionalIntro: "", personalPhoto: null });

export default {
    name: "ProfessionalProfileEditDialog",
    components: { RolePortraitUpload },
    props: {
        visible: { type: Boolean, default: false },
        professionalType: { type: String, required: true },
        profile: { type: Object, default: () => ({}) },
        portraitStatus: { type: Object, default: () => ({ canSubmit: false, message: "" }) },
    },
    data() {
        return { form: emptyForm(), submitting: false, smsSending: false, smsCooldown: 0, smsTimer: null, formError: "", openSelect: "" };
    },
    computed: {
        professionalApi() { return API.creatorCenter[this.professionalType]; },
        provinceOptions() { return Array.isArray(regionData) ? regionData : []; },
        selectedProvince() { return this.findOption(this.provinceOptions, this.form.provinceCode); },
        cityOptions() { return this.selectedProvince && Array.isArray(this.selectedProvince.children) ? this.selectedProvince.children : []; },
        selectedCity() { return this.findOption(this.cityOptions, this.form.cityCode); },
        districtOptions() { return this.selectedCity && Array.isArray(this.selectedCity.children) ? this.selectedCity.children : []; },
        selectedDistrict() { return this.findOption(this.districtOptions, this.form.districtCode); },
        smsButtonText() { return this.smsCooldown > 0 ? `${this.smsCooldown}s 后重试` : (this.smsSending ? "发送中…" : "获取验证码"); },
    },
    watch: {
        visible: { immediate: true, handler(nextVisible) { if (nextVisible) this.resetForm(this.profile); } },
    },
    beforeDestroy() { if (this.smsTimer) window.clearInterval(this.smsTimer); },
    methods: {
        findOption(options, value) { return (options || []).find((item) => String(item.value) === String(value)) || null; },
        selectedOptionLabel(options, value, placeholder) { const option = this.findOption(options, value); return option ? option.label : placeholder; },
        toggleSelect(name) { this.openSelect = this.openSelect === name ? "" : name; },
        selectProvince(value) { this.form.provinceCode = value; this.form.cityCode = ""; this.form.districtCode = ""; this.openSelect = ""; },
        selectCity(value) { this.form.cityCode = value; this.form.districtCode = ""; this.openSelect = ""; },
        selectDistrict(value) { this.form.districtCode = value; this.openSelect = ""; },
        resetForm(profile = {}) {
            this.form = { phone: profile.phone || "", smsCode: "", provinceCode: profile.provinceCode || "", cityCode: profile.cityCode || "", districtCode: profile.districtCode || "", professionalIntro: profile.professionalIntro || "", personalPhoto: null };
            this.formError = "";
            this.openSelect = "";
        },
        requestSmsCode() {
            if (!/^1[3-9]\d{9}$/.test(this.form.phone)) { this.formError = "请先填写有效的中国大陆手机号。"; return; }
            this.smsSending = true;
            this.formError = "";
            request.post(API.creatorCenter.smsCode, qs.stringify({
                phone: this.form.phone,
                professionalType: this.professionalType === "photographer" ? 1 : 2,
            }))
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    const smsCode = developmentSmsCode.extractDevelopmentSmsCode(response.data);
                    if (smsCode) this.form.smsCode = smsCode;
                    this.$message.success(getResultMessage(response, "验证码已发送到当前填写的手机号。"));
                    this.startSmsCooldown();
                })
                .catch((error) => { this.formError = getResultMessage(error, "验证码发送失败，请稍后重试。"); })
                .finally(() => { this.smsSending = false; });
        },
        startSmsCooldown() {
            if (this.smsTimer) window.clearInterval(this.smsTimer);
            this.smsCooldown = 60;
            this.smsTimer = window.setInterval(() => { this.smsCooldown -= 1; if (this.smsCooldown <= 0) { window.clearInterval(this.smsTimer); this.smsTimer = null; } }, 1000);
        },
        validateForm() {
            if (!/^1[3-9]\d{9}$/.test(this.form.phone)) return "请填写有效的中国大陆手机号。";
            if (!/^\d{6}$/.test(this.form.smsCode)) return "请填写短信验证码。";
            if (!this.selectedProvince || !this.selectedCity || !this.selectedDistrict) return "请选择完整的省、市、区／县。";
            const intro = this.form.professionalIntro.trim();
            if (!intro || intro.length > 2000) return "专业简介长度须为 1–2000 字。";
            return "";
        },
        buildPayload() {
            return { phone: this.form.phone, smsCode: this.form.smsCode, provinceCode: this.selectedProvince.value, cityCode: this.selectedCity.value, districtCode: this.selectedDistrict.value, provinceName: this.selectedProvince.label, cityName: this.selectedCity.label, districtName: this.selectedDistrict.label, professionalIntro: this.form.professionalIntro.trim() };
        },
        submitProfile() {
            this.formError = this.validateForm();
            if (this.formError || this.submitting) return;
            this.submitting = true;
            const submission = buildProfileUpdateSubmission(this.buildPayload(), this.form.personalPhoto, FormData);
            request.post(this.professionalApi.profile, submission.payload, submission.config)
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.$message.success(getResultMessage(response, "基础资料已即时更新。"));
                    this.$emit("submitted", getResultData(response));
                    this.$emit("close");
                })
                .catch((error) => { this.formError = getResultMessage(error, "基础资料保存失败，请稍后重试。"); })
                .finally(() => { this.submitting = false; });
        },
        closeDialog() { if (!this.submitting) this.$emit("close"); },
    },
};
</script>

<style scoped>
.profile-edit-overlay { position: fixed; z-index: 76; inset: 0; display: grid; place-items: center; padding: 24px; background: rgba(32, 27, 41, .56); }.profile-edit-dialog { width: min(620px, calc(100vw - 48px)); overflow: hidden; color: #332d49; background: #fff; border: 1px solid #d9e9f1; border-radius: 20px; box-shadow: 0 24px 70px rgba(30, 42, 58, .28); }.profile-edit-dialog > header { display: flex; gap: 18px; align-items: flex-start; justify-content: space-between; padding: 19px 22px 16px; border-bottom: 1px solid #e7eff3; }.profile-edit-dialog header p { margin: 0 0 4px; color: #5591ae; font-size: 10px; font-weight: 800; letter-spacing: .12em; }.profile-edit-dialog h3 { margin: 0 0 5px; color: #3f3158; font-size: 20px; }.profile-edit-dialog header span { color: #746c7e; font-size: 12px; line-height: 1.6; }.profile-edit-dialog header button { display: grid; width: 30px; height: 30px; place-items: center; padding: 0; color: #5d6070; background: #f1f6f8; border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 22px; }.profile-edit-dialog form main { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 15px; padding: 22px; }.profile-edit-dialog label { display: grid; gap: 7px; color: #5b5362; font-size: 12px; font-weight: 700; }.profile-edit-dialog label > span b { color: #dc657f; }.profile-edit-dialog input, .profile-edit-dialog select, .profile-edit-dialog textarea { min-width: 0; padding: 9px 10px; color: #514a5a; border: 1px solid #dbe3e8; border-radius: 8px; font: inherit; }.profile-edit-dialog textarea { resize: vertical; }.profile-edit-dialog label small { color: #857f76; text-align: right; font-size: 10px; font-weight: 400; }.profile-edit-full { grid-column: 1 / -1; }.profile-edit-code { display: flex; gap: 7px; }.profile-edit-code input { width: 0; flex: 1; }.profile-edit-code button { flex: 0 0 auto; padding: 0 9px; color: #3e7f9e; background: #fff; border: 1px solid #bcd9e6; border-radius: 8px; cursor: pointer; font: inherit; font-size: 11px; font-weight: 800; }.profile-edit-code button:disabled, footer button:disabled { cursor: not-allowed; opacity: .6; }.profile-edit-error { grid-column: 1 / -1; margin: 0; color: #a1495c; font-size: 12px; line-height: 1.6; }.profile-edit-loading { display: block !important; color: #857f76; font-size: 12px; }.profile-edit-dialog footer { display: flex; justify-content: flex-end; gap: 9px; padding: 14px 22px; border-top: 1px solid #e7eff3; }.profile-edit-dialog footer button { min-height: 38px; padding: 8px 15px; color: #5b7382; background: #fff; border: 1px solid #c9dce5; border-radius: 9px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 800; }.profile-edit-dialog footer .profile-edit-primary { color: #fff; background: #4f90ae; border-color: #4f90ae; } @media (max-width: 620px) { .profile-edit-dialog form main { grid-template-columns: 1fr; }.profile-edit-full { grid-column: auto; } }
.profile-edit-dialog input,
.profile-edit-dialog select,
.profile-edit-dialog textarea,
.profile-edit-dialog option {
    color: #514a5a;
    font-weight: 400;
}

.profile-edit-dialog input::placeholder,
.profile-edit-dialog textarea::placeholder {
    color: #a8a3ad;
    font-weight: 400;
    opacity: 1;
}

.profile-edit-dialog input:focus,
.profile-edit-dialog select:focus,
.profile-edit-dialog textarea:focus {
    border-color: #dbe3e8;
    outline: 0;
    box-shadow: none;
}

.profile-edit-dialog input,
.profile-edit-dialog select,
.profile-edit-dialog textarea,
.profile-edit-dialog option {
    font-weight: 400 !important;
}

.profile-edit-dialog > header > button {
    flex: 0 0 30px;
    line-height: 1 !important;
    text-align: center;
}

.profile-edit-dialog {
    display: flex;
    height: min(720px, calc(100vh - 48px));
    max-height: calc(100vh - 48px);
    flex-direction: column;
}

.profile-edit-dialog > header,
.profile-edit-dialog footer {
    flex: 0 0 auto;
}

.profile-edit-dialog form {
    display: flex;
    min-height: 0;
    flex: 1;
    flex-direction: column;
}

.profile-edit-dialog form main {
    min-height: 0;
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
}

@media (max-width: 620px) {
    .profile-edit-overlay {
        padding: 12px;
    }

    .profile-edit-dialog {
        width: 100%;
        height: calc(100vh - 24px);
        max-height: calc(100vh - 24px);
        border-radius: 16px;
    }
}

.profile-edit-select { position: relative; }
.profile-edit-select-trigger {
    display: flex;
    width: 100%;
    min-height: 38px;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 11px;
    color: #332d49;
    background: #fff;
    border: 1px solid #c5dce7;
    border-radius: 11px;
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    font-weight: 400 !important;
    text-align: left;
    transition: border-color 160ms ease;
}
.profile-edit-select-trigger:hover, .profile-edit-select-trigger[aria-expanded="true"] { border-color: #6fa9c3; }
.profile-edit-select-trigger:disabled { cursor: not-allowed; color: #aaa3ae; background: #f5f5f7; border-color: #d9d5dc; }
.profile-edit-select-trigger.is-placeholder { color: #a8a3ad; }
.profile-edit-select-trigger i { color: #6692a8; font-size: 16px; font-style: normal; font-weight: 400; line-height: 1; }
.profile-edit-select-menu { position: absolute; z-index: 8; top: calc(100% + 7px); right: 0; left: 0; max-height: 216px; padding: 6px; overflow-y: auto; overscroll-behavior: contain; background: #fff; border: 1px solid #bfdae6; border-radius: 13px; box-shadow: 0 12px 24px rgba(71, 118, 143, .12); }
.profile-edit-select-menu button { display: flex; width: 100%; min-height: 34px; align-items: center; justify-content: space-between; gap: 8px; padding: 7px 9px; color: #4e5962; background: transparent; border: 0; border-radius: 8px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 400; text-align: left; }
.profile-edit-select-menu button:hover { color: #3e7896; background: #eff8fc; }
.profile-edit-select-menu button.active { color: #326d8a; background: #e4f4fb; }
.profile-edit-select-menu button i { color: #4c93b4; font-style: normal; }
.profile-edit-dialog--makeup { border-color: #efc8d3; }
.profile-edit-dialog--makeup > header { border-bottom-color: #f3dde3; }
.profile-edit-dialog--makeup header p { color: #c66a83; }
.profile-edit-dialog--makeup input, .profile-edit-dialog--makeup select, .profile-edit-dialog--makeup textarea { border-color: #ebc7d1; }
.profile-edit-dialog--makeup input:focus, .profile-edit-dialog--makeup select:focus, .profile-edit-dialog--makeup textarea:focus { border-color: #d56886; }
.profile-edit-dialog--makeup .profile-edit-code button { color: #b85d76; border-color: #edbdc9; }
.profile-edit-dialog--makeup footer { border-top-color: #f3dde3; }
.profile-edit-dialog--makeup footer button { color: #a95570; border-color: #edbdc9; }
.profile-edit-dialog--makeup footer .profile-edit-primary { background: #d56886; border-color: #d56886; }
.profile-edit-dialog--makeup .profile-edit-select-trigger { border-color: #ebc7d1; }
.profile-edit-dialog--makeup .profile-edit-select-trigger:hover, .profile-edit-dialog--makeup .profile-edit-select-trigger[aria-expanded="true"] { border-color: #d56886; }
.profile-edit-dialog--makeup .profile-edit-select-trigger i { color: #c66a83; }
.profile-edit-dialog--makeup .profile-edit-select-menu { border-color: #edbdc9; box-shadow: 0 12px 24px rgba(190, 92, 119, .12); }
.profile-edit-dialog--makeup .profile-edit-select-menu button:hover { color: #a95570; background: #fff1f4; }
.profile-edit-dialog--makeup .profile-edit-select-menu button.active { color: #a0475c; background: #ffe8ee; }
.profile-edit-dialog--makeup .profile-edit-select-menu button i { color: #c66a83; }
</style>
